/**
 * ADR-0185 §3.6 — the what-changed strip: never a silent repaint. After a
 * re-solve lands (and in the what-if frame as the diff banner vs the standing
 * plan): jobs moved, setup-hours delta, promises newly at risk / cleared, and
 * the book's own drift (jobs that entered or left overnight — reported apart
 * so it never masquerades as movement).
 */
import { useState } from "react";
import type { VersusPrevious } from "./boardTypes.js";

export function WhatChangedStrip(props: {
  versus: VersusPrevious;
  tone: "update" | "scenario";
  onDismiss?: () => void;
}) {
  const [showMoves, setShowMoves] = useState(false);
  const v = props.versus;
  const delta = v.setup_hours_delta;
  const parts: string[] = [];
  parts.push(`${v.moved_count} job${v.moved_count === 1 ? "" : "s"} moved`);
  if (delta != null) {
    parts.push(
      delta === 0
        ? "setup hours unchanged"
        : delta < 0
          ? `${Math.abs(delta)} fewer setup hours`
          : `${delta} more setup hours`
    );
  }
  parts.push(
    `${v.promises_newly_at_risk.length} promise${v.promises_newly_at_risk.length === 1 ? "" : "s"} newly at risk`
  );
  if (v.promises_cleared.length > 0) parts.push(`${v.promises_cleared.length} cleared`);
  if (v.jobs_added.length > 0) parts.push(`${v.jobs_added.length} job${v.jobs_added.length === 1 ? "" : "s"} entered the book`);
  if (v.jobs_departed.length > 0) parts.push(`${v.jobs_departed.length} left it`);

  return (
    <div className={props.tone === "scenario" ? "boardStrip boardStripScenario" : "boardStrip"} role="status">
      <span>
        <strong>{props.tone === "scenario" ? "Versus the standing plan:" : "What changed:"}</strong> {parts.join(" · ")}
      </span>
      <span className="boardStripActions">
        {v.jobs_moved.length > 0 ? (
          <button className="adminListAction" onClick={() => setShowMoves((s) => !s)} aria-expanded={showMoves}>
            {showMoves ? "Hide moves" : "Show moves"}
          </button>
        ) : null}
        {props.onDismiss ? (
          <button className="adminListAction" onClick={props.onDismiss}>
            Dismiss
          </button>
        ) : null}
      </span>
      {showMoves ? (
        <div className="boardStripMoves">
          {v.jobs_moved.map((m) => (
            <div key={m.job}>
              <span className="mono">{m.job}</span>
              {m.part ? <span className="muted"> · {m.part}</span> : null}{" "}
              {m.from_machine === m.to_machine
                ? `stays on ${m.to_machine}, position ${m.from_position + 1} → ${m.to_position + 1}`
                : `${m.from_machine} → ${m.to_machine}`}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
